/**
 * Shop Notification Service
 *
 * Manages shop-wide notifications stored in Firestore.
 * These are internal notifications for staff (not client-facing).
 *
 * Types of notifications:
 * - New booking
 * - Cancelled appointment
 * - Walk-in arrival
 * - Low inventory alert
 * - Emergency booking request
 * - Staff clock-in/out
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface ShopNotification {
  id: string;
  type: 'booking' | 'cancellation' | 'walkin' | 'inventory' | 'emergency' | 'staff' | 'system';
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'urgent';
  read: boolean;
  createdAt: Date;
  expiresAt?: Date;
  link?: string; // Optional link to navigate to
  metadata?: Record<string, any>;
}

class ShopNotificationService {
  private static instance: ShopNotificationService;
  private collectionRef = collection(db, 'shop_notifications');

  private constructor() {}

  static getInstance(): ShopNotificationService {
    if (!ShopNotificationService.instance) {
      ShopNotificationService.instance = new ShopNotificationService();
    }
    return ShopNotificationService.instance;
  }

  /**
   * Subscribe to unread notifications (real-time)
   */
  subscribeToNotifications(
    callback: (notifications: ShopNotification[]) => void,
    options?: { limit?: number; includeRead?: boolean }
  ): () => void {
    const maxLimit = options?.limit || 50;

    let q = query(
      this.collectionRef,
      orderBy('createdAt', 'desc'),
      limit(maxLimit)
    );

    if (!options?.includeRead) {
      q = query(
        this.collectionRef,
        where('read', '==', false),
        orderBy('createdAt', 'desc'),
        limit(maxLimit)
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications: ShopNotification[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          type: data.type,
          title: data.title,
          message: data.message,
          severity: data.severity || 'info',
          read: data.read || false,
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate(),
          link: data.link,
          metadata: data.metadata,
        });
      });

      // Filter out expired notifications
      const now = new Date();
      const activeNotifications = notifications.filter(
        n => !n.expiresAt || n.expiresAt > now
      );

      callback(activeNotifications);
    });

    return unsubscribe;
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const q = query(
      this.collectionRef,
      where('read', '==', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  }

  /**
   * Create a new notification
   */
  async createNotification(notification: Omit<ShopNotification, 'id' | 'createdAt' | 'read'>): Promise<string> {
    const docRef = await addDoc(this.collectionRef, {
      ...notification,
      read: false,
      createdAt: Timestamp.now(),
      expiresAt: notification.expiresAt ? Timestamp.fromDate(notification.expiresAt) : null,
    });

    console.log(`🔔 Created notification: ${notification.title}`);
    return docRef.id;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    const docRef = doc(db, 'shop_notifications', notificationId);
    await updateDoc(docRef, { read: true });
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    const q = query(this.collectionRef, where('read', '==', false));
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { read: true });
    });

    await batch.commit();
    console.log(`✅ Marked ${snapshot.size} notifications as read`);
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    await deleteDoc(doc(db, 'shop_notifications', notificationId));
  }

  /**
   * Delete old/expired notifications (cleanup)
   */
  async cleanupOldNotifications(daysOld: number = 7): Promise<number> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const q = query(
      this.collectionRef,
      where('createdAt', '<', Timestamp.fromDate(cutoffDate)),
      where('read', '==', true)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    snapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    console.log(`🧹 Cleaned up ${snapshot.size} old notifications`);
    return snapshot.size;
  }

  // ============================================================================
  // HELPER METHODS FOR COMMON NOTIFICATIONS
  // ============================================================================

  /**
   * Notify about a new booking
   */
  async notifyNewBooking(clientName: string, serviceName: string, time: string, barberName: string): Promise<void> {
    await this.createNotification({
      type: 'booking',
      title: 'New Booking',
      message: `${clientName} booked ${serviceName} at ${time} with ${barberName}`,
      severity: 'info',
      expiresAt: this.getExpiryDate(24), // Expire after 24 hours
      metadata: { clientName, serviceName, time, barberName },
    });
  }

  /**
   * Notify about a cancellation
   */
  async notifyCancellation(clientName: string, serviceName: string, time: string): Promise<void> {
    await this.createNotification({
      type: 'cancellation',
      title: 'Appointment Cancelled',
      message: `${clientName} cancelled ${serviceName} at ${time}`,
      severity: 'warning',
      expiresAt: this.getExpiryDate(12), // Expire after 12 hours
      metadata: { clientName, serviceName, time },
    });
  }

  /**
   * Notify about a walk-in
   */
  async notifyWalkIn(clientName: string): Promise<void> {
    await this.createNotification({
      type: 'walkin',
      title: 'Walk-in Arrival',
      message: `${clientName} has arrived as a walk-in`,
      severity: 'info',
      expiresAt: this.getExpiryDate(2), // Expire after 2 hours
      metadata: { clientName },
    });
  }

  /**
   * Notify about low inventory
   */
  async notifyLowInventory(productName: string, currentStock: number, reorderLevel: number): Promise<void> {
    await this.createNotification({
      type: 'inventory',
      title: 'Low Inventory Alert',
      message: `${productName} is low on stock (${currentStock} remaining, reorder at ${reorderLevel})`,
      severity: 'warning',
      link: '/inventory',
      metadata: { productName, currentStock, reorderLevel },
    });
  }

  /**
   * Notify about emergency booking request
   */
  async notifyEmergencyRequest(clientName: string, urgency: string): Promise<void> {
    await this.createNotification({
      type: 'emergency',
      title: 'Emergency Booking Request',
      message: `${clientName} needs an urgent appointment (${urgency} priority)`,
      severity: 'urgent',
      expiresAt: this.getExpiryDate(4), // Expire after 4 hours
      metadata: { clientName, urgency },
    });
  }

  /**
   * Helper to calculate expiry date
   */
  private getExpiryDate(hoursFromNow: number): Date {
    const date = new Date();
    date.setHours(date.getHours() + hoursFromNow);
    return date;
  }
}

export const shopNotificationService = ShopNotificationService.getInstance();
export default shopNotificationService;
